import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, combineLatest, map, Subscription, take, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BreadcrumbComponent } from "../../shared/components/breadcrumb/breadcrumb.component";
import { OverlayLoadingSpinnerBorderComponent } from "../../shared/components/loading/overlay-loading-spinner-border/overlay-loading-spinner-border.component";
import { MediaDirective } from '../../shared/directives/media-directive';
import { SchemaStatus } from '../../shared/enums/schema-status';
import { calculatePaginationPages } from '../../shared/helpers/pagination-helper';
import { Schema } from '../../shared/models/schema.model';
import { DateFormatPipe } from "../../shared/pipes/date-format.pipe";
import { DatetimeFormatPipe } from "../../shared/pipes/datetime-format.pipe";
import { SchemaStatusPipe } from "../../shared/pipes/schema-status.pipe";
import { SchemaRepository } from '../../shared/repositories/schema.repository';
import { SessionStoreRepository } from '../../shared/repositories/session.repository';
import { NewSchemaDialogComponent } from "./new-schema-dialog/new-schema-dialog.component";

export interface SearchInputState {
    archiveCpr?: string;
    searchString?: string;
}

const lastSearchQuery = { value: null as string | null };

@Component({
    selector: 'doctor-schemas',
    templateUrl: './doctor-schemas.component.html',
    imports: [CommonModule, TranslocoDirective, NewSchemaDialogComponent, DateFormatPipe, SchemaStatusPipe, MediaDirective, DatetimeFormatPipe, BreadcrumbComponent, OverlayLoadingSpinnerBorderComponent]
})

export class DoctorSchemasComponent {
    private readonly translocoService = inject(TranslocoService);
    private readonly sessionService = inject(SessionStoreRepository);
    private readonly router = inject(Router);
    private readonly schemaService = inject(SchemaRepository);

    private subscription = new Subscription();

    schemas$ = this.schemaService.clinicSchemas$;
    isPatient$ = this.sessionService.isPatient$;
    isLoading$ = this.schemaService.clinicSchemasIsLoading$;
    paginatedSchemas$ = new BehaviorSubject<Schema[]>([]);
    searchQuery$ = new BehaviorSubject<string>('');
    sortColumn$ = new BehaviorSubject<string>('');
    sortDirection$ = new BehaviorSubject<string>('');
    showLoadingSignal = toSignal(this.schemaService.clinicSchemasIsLoading$, { initialValue: false });
    SchemaStatus = SchemaStatus;
    isProduction: boolean = true;

    currentPage: number = 1;
    perPage: number = 10;
    lastPage: number = 1;
    pages: number = 1;
    total: number = 0;
    paginationInterval: number = 5;
    visiblePaginationPages: number[] = [];

    ngOnInit() {
        this.isProduction = environment.production;
        this.subscription.add(
            this.schemaService.getClinicSchemas$().subscribe()
        );
        this.subscription.add(
            combineLatest([this.schemas$, this.searchQuery$, this.sortColumn$, this.sortDirection$]).pipe(
                map(([schemas, query, sortColumn, sortDirection]) =>
                    this.filterSchemas(schemas, query, sortColumn, sortDirection)),
                tap(filteredSchemas => this.updatePagination(filteredSchemas))
            ).subscribe()
        );

        const webarchiveState: SearchInputState = history.state;
        const initialQuery = webarchiveState?.searchString || webarchiveState?.archiveCpr || lastSearchQuery.value || '';
        this.searchQuery$.next(initialQuery);
        lastSearchQuery.value = initialQuery;

        const savedPage = sessionStorage.getItem('schemaTablePage');
        this.currentPage = savedPage ? +savedPage : 1;
        this.updatePaginatedSchemas();
        this.onChangeScrollTo();
    }

    ngOnDestroy(): void {
        this.unsubscribe();
        this.removeModalBackdropIfPresent();
    }

    onSchemaClicked(schemaId: number, schemaCount: number, schemaStatus: string) {
        this.isPatient$.pipe(take(1)).subscribe(isPatient => {
            if (!(schemaStatus == SchemaStatus.EXPIRED && isPatient)) {
                this.router.navigate(["schema", schemaId]);
            }
        });
    }

    changePage(page: number) {
        if (page < 1 || page > this.lastPage) return;

        this.currentPage = page;
        sessionStorage.setItem('schemaTablePage', page.toString());
        this.updatePaginatedSchemas();
        this.onChangeScrollTo();
    }

    updatePaginatedSchemas(filteredSchemas?: Schema[]) {
        if (!filteredSchemas) {
            this.subscription.add(
                combineLatest([this.schemas$, this.searchQuery$, this.sortColumn$, this.sortDirection$]).pipe(
                    map(([schemas, query, sortColumn, sortDirection]) => 
                        this.filterSchemas(schemas, query, sortColumn, sortDirection)),
                    tap(filteredSchemas => this.paginate(filteredSchemas))
                ).subscribe()
            );
        } else {
            this.paginate(filteredSchemas);
        }
    }

    updatePagination(filteredSchemas: Schema[]) {
        this.total = filteredSchemas.length;
        this.lastPage = Math.ceil(this.total / this.perPage);
        this.pages = Math.ceil(this.total / this.perPage);
        this.updatePaginatedSchemas(filteredSchemas);
    }

    updateSchemaStore(): void {
        this.schemas$ = this.schemaService.clinicSchemas$;
    }

    onSearch() {
        const searchInput = (document.querySelector('.search-input') as HTMLInputElement).value;
        this.searchQuery$.next(searchInput);
        lastSearchQuery.value = searchInput;

        const { archiveCpr, ...state } = history.state ?? {};
        const modifiedState: SearchInputState = { ...state, searchString: searchInput };
        history.replaceState(modifiedState, '');

        this.currentPage = 1;
        sessionStorage.setItem('schemaTablePage', "1");
        this.updatePaginatedSchemas();
    }

    private filterSchemas(schemas: Schema[], query: string, sortColumn: string, sortDirection: string): Schema[] {
        const filtered = schemas.filter(schema =>
            schema.patientCpr.includes(query) ||
            schema.patientName.toLowerCase().includes(query.toLowerCase()) ||
            schema.schemaTypeName.toLowerCase().includes(query.toLowerCase())
        )

        if (sortColumn) {
            const translations = this.translocoService.translateObject("schema") as Record<string, string>;
            filtered.sort((a: any, b: any) => {
                const aValue = sortColumn == "schemaStatus" ?
                    translations[a[sortColumn].toLowerCase()] :
                    a[sortColumn];
                const bValue = sortColumn == "schemaStatus" ?
                    translations[b[sortColumn].toLowerCase()] :
                    b[sortColumn];

                // Handle null/undefined
                if (aValue == null && bValue == null) return 0;
                if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
                if (bValue == null) return sortDirection === 'asc' ? 1 : -1;

                // Handle strings and numbers differently
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return sortDirection === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                // Handle dates or numbers
                return sortDirection === 'asc'
                    ? aValue > bValue ? 1 : -1
                    : aValue < bValue ? 1 : -1;
            });
        }

        return filtered;
    }

    private paginate(filteredSchemas: Schema[]) {
        const paginatedData = filteredSchemas.slice((this.currentPage - 1) * this.perPage, this.currentPage * this.perPage);
        this.paginatedSchemas$.next(paginatedData);
        this.updatePaginationBar();
    }

    private updatePaginationBar() {
        this.visiblePaginationPages = calculatePaginationPages(this.currentPage, this.lastPage, this.pages, this.paginationInterval);
    }

    private removeModalBackdropIfPresent(): void {
        const modalBackdrop = document.querySelector('.modal-backdrop');
        if (modalBackdrop) {
            modalBackdrop.remove();
        }
    }

    private onChangeScrollTo() {
        const element = document.querySelector(".scroll-to");
        element?.scrollIntoView({ behavior: "instant" });
    }

    private unsubscribe(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    sortBy(column: keyof Schema) {
        if (this.sortColumn$.value === column) {
            const newDir = this.sortDirection$.value === 'asc' ? 'desc' : 'asc';
            this.sortDirection$.next(newDir);
        } else {
            this.sortColumn$.next(column);
            this.sortDirection$.next('asc');
        }

        this.currentPage = 1;
        sessionStorage.setItem('schemaTablePage', this.currentPage.toString());
    }
}
